import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../assets/images/common/closeIcon.svg";
import useAnalytics from "../utils/useAnalytics";
import FooterCoupon from "../components/FooterCoupon";
import SwipeButton from "../components/Buttons/SwipeButton";
import couponMainActions from "../components/modules/couponMainActions";
import ActionSheet from "../components/ActionSheet/ActionSheet";
import {
  Container,
  BaseWrapper,
  HeaderBorder,
  LogoContainer,
  TitleContainer,
  Title,
  DescriptionContainer,
  Description,
  ButtonContainer,
  CloseButton,
  GlobalStyles,
} from "../components/styles/CouponStyles";

const getLocalizedText = (items, language, fallbackLanguage = "en") => {
  return (
    items?.find((x) => x.language === language)?.text ||
    items?.find((x) => x.language === fallbackLanguage)?.text
  );
};

const MobileCoupon = () => {
  const { token: locationId, lang } = useParams();
  const language = lang === "es" ? "sp" : lang;
  const sheetRef = useRef();

  const [couponData, setCouponData] = useState(null);
  const [contentVisible, setContentVisible] = useState(true);
  const [error, setError] = useState(null);

  const { sendCommEngagementEvent } = useAnalytics(
    couponData?.GATrackingNumber
  );

  const handleOpen = useCallback(() => {
    sheetRef.current?.open();
  }, []);

  const handleClose = useCallback(() => {
    window.top.postMessage("matador_coupon_message_hidden", "*");
    setContentVisible(false);
    sheetRef.current?.close();
  }, []);

  const GAEventTracking = useCallback(() => {
    sendCommEngagementEvent({ widget: "Mobile Coupon", status: "start" });
  }, [sendCommEngagementEvent]);

  useEffect(() => {
    if (couponData) {
      handleOpen();
      sendCommEngagementEvent({ widget: "Mobile Coupon", status: "load" });
    }
  }, [couponData, handleOpen, sendCommEngagementEvent]);

  useEffect(() => {
    const fetchCouponData = async () => {
      try {
        if (!locationId) return;

        const result = await couponMainActions.getCoupon(locationId);
        if (result.success) {
          setCouponData(result.data);
        } else {
          setError("Failed to load coupon data");
        }
      } catch (err) {
        setError(err.message || "An error occurred while fetching coupon data");
      }
    };

    fetchCouponData();
  }, [locationId]);

  if (error) return null;
  if (!couponData) return null;

  const localizedContent = {
    title: getLocalizedText(couponData.title, language),
    description: getLocalizedText(couponData.description, language),
    buttonTexts: getLocalizedText(couponData.buttonTexts, language),
    smsText: getLocalizedText(couponData.smsTemplate, language),
  };

  const renderContent = () => (
    <BaseWrapper backgroundColor={couponData.primaryBackgroundColor}>
      <HeaderBorder />
      <CloseButton onClick={handleClose} isMobile>
        <CloseIcon stroke={couponData.titleColor} />
      </CloseButton>

      <LogoContainer>
        {couponData.logoImageUrl && (
          <img src={couponData.logoImageUrl} alt="Logo" />
        )}
      </LogoContainer>

      <TitleContainer>
        {localizedContent.title && (
          <Title color={couponData.titleColor}>{localizedContent.title}</Title>
        )}
      </TitleContainer>

      <DescriptionContainer>
        {localizedContent.description && (
          <Description color={couponData.descriptionColor}>
            {localizedContent.description}
          </Description>
        )}
      </DescriptionContainer>

      <ButtonContainer>
        <SwipeButton
          backgroundColor={couponData.backgroundColor}
          color={couponData.buttonColor}
          text={localizedContent.buttonTexts}
          phoneNumber={couponData.organization_number}
          smsText={localizedContent.smsText}
          onSuccess={handleClose}
          lang={language}
          eventTracking={GAEventTracking}
        />
      </ButtonContainer>

      <FooterCoupon
        lang={language}
        termsLink={couponData.termsConditionsLink}
        theme={couponData.theme}
        locationId={couponData._location_id}
      />
    </BaseWrapper>
  );

  if (!contentVisible) return null;

  return (
    <>
      <GlobalStyles />
      <ActionSheet
        closeOnBgTap={false}
        sheetStyle={{ backgroundColor: "transparent" }}
        ref={sheetRef}
        onClose={handleClose}
      >
        <Container isMobile>{renderContent()}</Container>
      </ActionSheet>
    </>
  );
};

export default React.memo(MobileCoupon);
